import React, { useState, useEffect } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/layout/Logo";
import { Loader2, ArrowUpRight } from "lucide-react";

export default function Login() {
  const { user, login, checking } = useAuth();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { document.title = "Staff Login · NJAS"; }, []);

  if (!checking && user && user.id) {
    const to = loc.state?.from || "/dashboard";
    return <Navigate to={to} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-njas-bg" data-testid="login-page">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:col-span-3 bg-njas-navy text-njas-bg flex-col justify-between p-14 relative">
        <Link to="/"><Logo tone="light" markSize={54} /></Link>
        <div>
          <div className="njas-eyebrow text-njas-sage">Staff Portal</div>
          <div className="njas-script text-5xl text-njas-sage mt-6">The team behind</div>
          <h2 className="njas-display text-7xl mt-2 leading-[0.88]">
            EVERY&nbsp;STORY<br/>OF&nbsp;CHANGE.
          </h2>
          <p className="mt-8 text-njas-bg/70 max-w-md text-sm leading-relaxed">
            Access referrals, case notes and coordinate care with your team. All in one place.
          </p>
        </div>
        <div className="njas-condensed text-[10px] tracking-[0.28em] text-njas-bg/40 font-semibold">
          Confidential portal · authorised access only
        </div>
      </div>

      {/* Right panel — form */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10"><Link to="/"><Logo /></Link></div>
          <div className="njas-eyebrow">Sign in</div>
          <h1 className="njas-display text-6xl text-njas-navy mt-4 leading-[0.9]">STAFF &amp; ADMIN.</h1>
          <p className="mt-4 text-njas-muted text-sm">Use the credentials issued by your NJAS administrator.</p>

          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="login-form">
            <label className="block">
              <span className="njas-eyebrow text-njas-navy block">Email</span>
              <input
                type="email" required autoComplete="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="w-full border-0 border-b border-[var(--njas-line-strong)] bg-transparent py-3 text-njas-text focus:outline-none focus:border-njas-sage transition"
              />
            </label>
            <label className="block">
              <span className="njas-eyebrow text-njas-navy block">Password</span>
              <input
                type="password" required autoComplete="current-password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="w-full border-0 border-b border-[var(--njas-line-strong)] bg-transparent py-3 text-njas-text focus:outline-none focus:border-njas-sage transition"
              />
            </label>

            {error && (
              <div className="border border-red-300 bg-red-50 text-red-800 px-4 py-3 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit" disabled={busy}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-3 bg-njas-navy text-njas-bg py-4 njas-condensed tracking-[0.22em] font-semibold text-[13px] hover:bg-njas-navy-hover transition-colors disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
              Sign In
            </button>
          </form>

          <div className="mt-10 pt-6 border-t border-[var(--njas-line)] text-xs text-njas-muted">
            Not staff? <Link to="/" className="njas-link text-njas-navy njas-condensed tracking-[0.18em] font-semibold ml-2">Back to the main site</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
